import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Calendar } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { useReveal } from "./Home";
import Seo from "../components/Seo";

const News = () => {
  const ref = useReveal();
  const navigate = useNavigate();
  const { t, lp } = useLang();
  const [featured, ...rest] = t.news.articles;

  return (
    <div ref={ref}>
      <Seo title={t.seo.news.title} description={t.seo.news.description} />
      <section className="relative overflow-hidden bg-[#0f1a10] pb-20 pt-40">
        <div className="absolute inset-0 opacity-20">
          <img src={t.hero.slides[3]} alt="" className="h-full w-full object-cover grayscale" />
        </div>
        <div className="relative mx-auto max-w-[1200px] px-6">
          <span className="text-sm font-semibold uppercase tracking-widest text-[#F26522]">{t.news.eyebrow}</span>
          <h1 className="mt-3 max-w-3xl text-4xl font-extrabold leading-tight text-white lg:text-5xl">{t.news.pageTitle}</h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-neutral-300">{t.news.pageSubtitle}</p>
        </div>
      </section>

      <section className="mx-auto max-w-[1200px] px-6 py-20 lg:py-24">
        <div
          onClick={() => navigate(lp(`/news/${featured.id}`))}
          className="reveal group grid cursor-pointer overflow-hidden rounded-2xl border border-neutral-100 bg-white shadow-sm transition-shadow duration-300 hover:shadow-xl lg:grid-cols-2"
        >
          <div className="aspect-video overflow-hidden lg:aspect-auto">
            <img src={featured.image} alt={featured.title} style={{ objectPosition: featured.imagePos || "center" }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          </div>
          <div className="flex flex-col justify-center p-8 lg:p-12">
            <div className="flex items-center gap-3 text-xs">
              <span className="rounded-full bg-[#F26522]/10 px-3 py-1 font-semibold text-[#F26522]">{featured.category}</span>
              <span className="flex items-center gap-1.5 text-neutral-500"><Calendar className="h-3.5 w-3.5" /> {featured.date}</span>
            </div>
            <h2 className="mt-4 text-2xl font-bold leading-snug text-neutral-900 lg:text-3xl">{featured.title}</h2>
            <p className="mt-4 text-[15px] leading-relaxed text-neutral-600">{featured.excerpt}</p>
            <span className="mt-6 inline-flex items-center gap-2 self-start text-sm font-semibold text-[#F26522]">
              {t.news.readMore}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </span>
          </div>
        </div>

        <div className="mt-10 grid gap-8 md:grid-cols-3">
          {rest.map((n, i) => (
            <article key={n.id} style={{ animationDelay: `${i * 80}ms` }}
              onClick={() => navigate(lp(`/news/${n.id}`))}
              className="reveal group cursor-pointer overflow-hidden rounded-xl border border-neutral-100 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl">
              <div className="aspect-video overflow-hidden">
                <img src={n.image} alt={n.title} style={{ objectPosition: n.imagePos || "center" }} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 text-xs">
                  <span className="rounded-full bg-[#F26522]/10 px-3 py-1 font-semibold text-[#F26522]">{n.category}</span>
                  <span className="flex items-center gap-1.5 text-neutral-500"><Calendar className="h-3.5 w-3.5" /> {n.date}</span>
                </div>
                <h3 className="mt-3 text-lg font-bold leading-snug text-neutral-900 transition-colors group-hover:text-[#F26522]">{n.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-600 line-clamp-3">{n.excerpt}</p>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default News;
